import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { getRole } from '../services/auth';

const STATUS_ORDER = ['lead', 'verified', 'visa_approved', 'enrolled'];
const STATUS_LABEL = { lead: 'Lead', verified: 'Verified', visa_approved: 'Visa Approved', enrolled: 'Enrolled' };


export default function UserDashboardPage() {
  const role = getRole();
  const navigate = useNavigate();
  const canSeeSummary = role === 'councillor' || role === 'admin';

  const [searchParams] = useSearchParams();
  const defaultTab = canSeeSummary ? (searchParams.get('tab') || 'summary') : 'referrals';
  const [tab, setTab]  = useState(defaultTab);

  useEffect(() => {
    const t = searchParams.get('tab');
    setTab(t || (canSeeSummary ? 'summary' : 'referrals'));
    setSearch('');
  }, [searchParams, canSeeSummary]);
  const [summary, setSummary]             = useState(null);
  const [referrals, setReferrals]       = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch]               = useState('');
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState('');
  const [actionLoading, setAL]            = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      if (tab === 'summary') {
        const { data } = await api.get('/referral/viewer/summary');
        setSummary(data);
      } else if (tab === 'referrals') {
        const url = statusFilter ? `/referral/viewer/referrals?status=${statusFilter}` : '/referral/viewer/referrals';
        const { data } = await api.get(url);
        setReferrals(data);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load data');
    } finally { setLoading(false); }
  }, [tab, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const fmtINR  = n => `₹${Number(n).toLocaleString('en-IN')}`;
  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  const markVerified = async (id) => {
    setAL(id);
    try {
      await api.put(`/referral/admin/referrals/${id}/status`, { status: 'verified' });
      setReferrals(prev => prev.map(r => r.id === id ? { ...r, status: 'verified' } : r));
    } catch (e) { alert(e.response?.data?.detail || 'Failed to update status'); }
    finally { setAL(null); }
  };

  const filteredReferrals = referrals.filter(r => {
    const q = search.toLowerCase();
    return !q || [r.referrer_name, r.referrer_email, r.referred_name, r.referred_email, r.referred_phone].some(v => v?.toLowerCase().includes(q));
  });

  return (
    <div style={S.page}>
      <div style={S.pageHeader}>
        <h1 style={S.pageTitle}>
          {tab === 'summary' ? '📊 Summary' : '🔗 Referrals'}
        </h1>
      </div>

      {error && <div style={S.errBox}>{error}</div>}

      {/* ── Summary ── */}
      {canSeeSummary && tab === 'summary' && !loading && summary && (
        <div style={S.summaryGrid}>
          {[
            { label: 'Referral Submissions', value: summary.referral_submissions, color: '#7c3aed', tab: 'referrals' },
            { label: 'Referred Students',    value: summary.referred_students,    color: '#0891b2', tab: 'referrals' },
            { label: 'Pending Verification', value: summary.pending_verification, color: '#f97316', tab: 'referrals' },
            { label: 'Total Councillors',    value: summary.total_councillors,    color: '#6d28d9', tab: 'referrals' },
            { label: 'Pending Payouts',      value: fmtINR(summary.total_pending),color: '#f59e0b', tab: 'referrals' },
            { label: 'Approved Rewards',     value: fmtINR(summary.total_approved),color: '#3b82f6', tab: 'referrals' },
            { label: 'Total Paid Out',       value: fmtINR(summary.total_paid),   color: '#10b981', tab: 'referrals' },
          ].map(c => (
            <div
              key={c.label}
              style={{ ...S.summaryCard, borderLeftColor: c.color, cursor: 'pointer' }}
              onClick={() => navigate(`/view?tab=${c.tab}`)}
            >
              <div style={S.summaryVal}>{c.value}</div>
              <div style={S.summaryLabel}>{c.label}</div>
              <div style={{ fontSize: '.72rem', color: '#94a3b8', marginTop: '.4rem' }}>Click to view →</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Referrals ── */}
      {tab === 'referrals' && (
        <>
          <div style={S.filterRow}>
            <label style={S.filterLabel}>Status:</label>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={S.select}>
              <option value="">All</option>
              {STATUS_ORDER.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>
            <input
              type="text"
              placeholder="Search referrer or referred…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={S.searchInput}
            />
            <span style={S.count}>{filteredReferrals.length} of {referrals.length} referrals</span>
          </div>
          {loading ? <Loader /> : (
            <TableWrap>
              <thead><tr style={S.thead}>
                {['Referrer', 'Referred Student', 'Status', 'Date', 'Action'].map(h => <Th key={h}>{h}</Th>)}
              </tr></thead>
              <tbody>
                {filteredReferrals.length === 0 && <EmptyRow cols={5} />}
                {filteredReferrals.map(r => (
                  <tr key={r.id} style={S.tr}>
                    <Td>
                      <b>{r.referrer_name || '—'}</b>
                      <br/><small style={S.small}>{r.referrer_email}</small>
                      <br/><small style={S.small}>{r.referrer_phone}</small>
                    </Td>
                    <Td>
                      <b>{r.referred_name || '—'}</b>
                      <br/><small style={S.small}>{r.referred_email}</small>
                      <br/><small style={S.small}>{r.referred_phone}</small>
                    </Td>
                    <Td>
                      <StatusBadge status={r.status} />
                      {r.rewards?.length > 0 && (
                        <div style={{ marginTop: '.35rem' }}>
                          {r.rewards.map((rw, i) => (
                            <div key={i} style={{ fontSize: '.75rem', display: 'flex', gap: '.3rem', alignItems: 'center', marginBottom: '.15rem' }}>
                              <span style={{ color: rw.status === 'paid' ? '#10b981' : rw.status === 'approved' ? '#3b82f6' : '#f59e0b' }}>●</span>
                              <span style={{ fontWeight: 600 }}>₹{rw.amount.toLocaleString('en-IN')}</span>
                              <span style={{ color: '#94a3b8' }}>{rw.stage.replace('_',' ')} · {rw.status}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </Td>
                    <Td><small style={S.small}>{fmtDate(r.created_at)}</small></Td>
                    <Td>
                      {r.status === 'lead'
                        ? <button
                            style={S.verifyBtn}
                            disabled={actionLoading === r.id}
                            onClick={() => markVerified(r.id)}
                          >
                            {actionLoading === r.id ? '…' : 'Mark Verified'}
                          </button>
                        : <span style={S.doneLabel}>✓ {r.status.replace('_',' ')}</span>}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </>
      )}

      {loading && tab === 'summary' && <Loader />}
    </div>
  );
}

function TableWrap({ children }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,.08)', overflow: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.88rem' }}>{children}</table>
    </div>
  );
}
const Th = ({ children }) => (
  <th style={{ padding: '.7rem 1rem', textAlign: 'left', color: '#64748b', fontSize: '.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{children}</th>
);
const Td = ({ children }) => (
  <td style={{ padding: '.8rem 1rem', verticalAlign: 'middle', borderBottom: '1px solid #f1f5f9' }}>{children}</td>
);
const EmptyRow = ({ cols }) => (
  <tr><td colSpan={cols} style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8' }}>No records found</td></tr>
);
const Loader = () => <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>Loading…</div>;

function StatusBadge({ status }) {
  const C = { lead: ['#f1f5f9','#475569'], verified: ['#dbeafe','#1d4ed8'], visa_approved: ['#ede9fe','#6d28d9'], enrolled: ['#dcfce7','#166534'] };
  const [bg, text] = C[status] || ['#f1f5f9','#475569'];
  return <span style={{ background: bg, color: text, padding: '.25rem .7rem', borderRadius: 20, fontSize: '.78rem', fontWeight: 600, textTransform: 'capitalize' }}>{status?.replace('_',' ')}</span>;
}

const S = {
  page:        { maxWidth: 1100, margin: '0 auto', padding: '2rem 1.25rem', fontFamily: "'Inter','Segoe UI',sans-serif" },
  pageHeader:  { marginBottom: '1.5rem' },
  pageTitle:   { fontSize: '1.6rem', fontWeight: 800, color: '#0f172a', margin: 0 },
  pageSub:     { color: '#64748b', fontSize: '.9rem', marginTop: '.3rem' },
  tabBar:      { display: 'flex', gap: '.4rem', marginBottom: '1.5rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '.5rem', flexWrap: 'wrap' },
  tabBtn:      { padding: '.45rem 1.1rem', borderRadius: '8px 8px 0 0', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '.88rem' },
  errBox:      { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '.75rem 1rem', borderRadius: 8, marginBottom: '1rem' },
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' },
  summaryCard: { background: '#fff', borderRadius: 12, padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,.08)', borderLeft: '4px solid #ccc' },
  summaryVal:  { fontSize: '1.5rem', fontWeight: 700, color: '#0f172a' },
  summaryLabel:{ fontSize: '.8rem', color: '#64748b', marginTop: '.25rem' },
  filterRow:   { display: 'flex', gap: '.75rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' },
  filterLabel: { fontSize: '.85rem', color: '#64748b', fontWeight: 600 },
  select:      { border: '1.5px solid #d1d5db', borderRadius: 8, padding: '.4rem .8rem', fontSize: '.9rem' },
  searchInput: { padding: '.45rem .9rem', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: '.9rem', color: '#1e293b', background: '#fff', outline: 'none', width: 260 },
  count:       { fontSize: '.85rem', color: '#94a3b8' },
  thead:       { background: '#f8fafc' },
  tr:          { borderBottom: '1px solid #f1f5f9' },
  small:       { color: '#64748b', fontSize: '.78rem' },
  verifyBtn:   { background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '.35rem .8rem', fontSize: '.8rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  doneLabel:   { fontSize: '.8rem', color: '#10b981', fontWeight: 600 },
};
